import { Card, CardContent, CardHeader } from '@/common/ui/card'
import { ScrollArea } from '@/common/ui/scroll-area'
import { GetAnnouncementResponse } from '@/modules/announcement/type'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Skeleton } from '@/common/ui/skeleton'
import { Link } from 'react-router-dom'
import { Edit } from 'lucide-react'
import DeleteAnnouncement from '../DeleteAnnouncement'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'

interface AnnouncementBoxProps {
	data: GetAnnouncementResponse
	fetchNextPage: () => void
	hasMore: boolean
}

export default function AnnouncementBox({
	data,
	fetchNextPage,
	hasMore,
}: AnnouncementBoxProps) {
	const { data: user } = useQueryProfile()
	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_ANNOUNCEMENT,
	)
	return (
		<ScrollArea className="h-full w-full p-4">
			{!data?.data || data.data.length === 0 ? (
				<div className="flex h-full w-full items-center justify-center">
					<p className="text-2xl font-bold text-gray-500">ยังไม่มีประกาศ</p>
				</div>
			) : (
				<InfiniteScroll
					dataLength={data?.data?.length || 0}
					next={fetchNextPage}
					hasMore={hasMore}
					loader={<LoadingSkeleton />}
					scrollableTarget="ScrollAreaViewport"
				>
					<div className="flex flex-col space-y-4">
						{data?.data?.map((announcement) => (
							<Card key={announcement.id}>
								<CardHeader>
									<div className="flex items-center justify-between">
										<h1 className="text-xl font-bold">{announcement.title}</h1>
										<div className="flex items-center gap-2">
											{hasWritePermission && (
												<>
													<DeleteAnnouncement id={announcement.id} />
													<Link to={`/operator/home-page/${announcement.id}`}>
														<Edit className="h-4 w-4" />
													</Link>
												</>
											)}
										</div>
									</div>
								</CardHeader>
								<CardContent>
									<div
										className="marker:text-red-400 [&>ul>li:last-child]:mb-0 [&>ul>li]:mb-2 [&>ul]:ml-6 [&>ul]:list-disc"
										dangerouslySetInnerHTML={{ __html: announcement.content }}
									/>
								</CardContent>
							</Card>
						))}
					</div>
				</InfiniteScroll>
			)}
		</ScrollArea>
	)
}

const LoadingSkeleton = () => (
	<div className="space-y-4">
		{[1, 2].map((i) => (
			<Card key={i}>
				<CardHeader>
					<Skeleton className="h-6 w-1/3" />
				</CardHeader>
				<CardContent>
					<div className="space-y-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-5/6" />
						<Skeleton className="h-4 w-4/6" />
					</div>
				</CardContent>
			</Card>
		))}
	</div>
)
